import { Link } from 'react-router-dom'
import { ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { ReactNode } from 'react'

interface Crumb {
  label: string
  to?: string
}

interface Props {
  title: string
  subtitle?: string
  description?: string
  actions?: ReactNode
  crumbs?: Crumb[]
  meta?: ReactNode
  className?: string
}

/**
 * PageHeader — cabecera consistente para todas las páginas (§6.4 plan).
 *
 * Estructura:
 *   [Migas opcionales]
 *   [Título grande]  [Acciones derecha]
 *   [Subtítulo]
 *   [Meta opcional - badges, contadores, etc.]
 *
 * No usar h1 múltiples. Cada página renderiza un único <PageHeader>.
 */
export function PageHeader({ title, subtitle, description, actions, crumbs, meta, className }: Props) {
  return (
    <header className={cn('mb-6 space-y-1.5', className)}>
      {crumbs && crumbs.length > 0 && (
        <nav aria-label="Ruta de navegación" className="flex items-center gap-1 text-xs text-muted-foreground">
          {crumbs.map((c, i) => (
            <span key={i} className="flex items-center gap-1">
              {i > 0 && <ChevronRight className="h-3 w-3 shrink-0" />}
              {c.to ? (
                <Link to={c.to} className="hover:text-foreground transition-colors">
                  {c.label}
                </Link>
              ) : (
                <span className="text-foreground/80">{c.label}</span>
              )}
            </span>
          ))}
        </nav>
      )}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl font-bold tracking-tight">{title}</h1>
          {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
          {description && <p className="text-sm text-muted-foreground mt-2 max-w-2xl">{description}</p>}
        </div>
        {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
      </div>
      {meta && <div className="mt-2 flex flex-wrap items-center gap-2">{meta}</div>}
    </header>
  )
}
